// frontend/src/components/landing/sections/Hero.js
import React from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { Chip, Container, Glow, LandingButton, REDUCED_MOTION, Reveal } from '../parts';
import { tokens } from '../landingTheme';

const wordIn = keyframes`
    from { opacity: 0; transform: translateY(0.35em); filter: blur(6px); }
    to   { opacity: 1; transform: none; filter: none; }
`;

const draw = keyframes`
    from { background-size: 0% 34%; }
    to   { background-size: 100% 34%; }
`;

const popIn = keyframes`
    from { opacity: 0; transform: rotate(var(--tilt)) scale(0.6); }
    to   { opacity: 1; transform: rotate(var(--tilt)) scale(1); }
`;

const bob = keyframes`
    0%, 100% { transform: translateY(0) rotate(var(--tilt)); }
    50%      { transform: translateY(-10px) rotate(var(--tilt)); }
`;

const LEAD_WORDS = ['See', 'how', 'talk', 'unfolds', 'in', 'your'];
const MARKED_WORDS = ['science', 'classroom.'];
const WORD_STEP = 0.07;
const DRAW_AT = 0.15 + (LEAD_WORDS.length + MARKED_WORDS.length) * WORD_STEP + 0.2;

// CDAT codes that float around the headline on desktop; peach ones echo the highlighted utterance
const FLOATING_CODES = [
    { code: 'Q', name: 'Question', left: '6%', top: '26%', tilt: '-6deg', duration: 7 },
    { code: 'Rs', name: 'Response with statement', left: '83%', top: '22%', tilt: '5deg', duration: 8 },
    { code: 'INF', name: 'Inference from data', left: '3%', top: '58%', tilt: '4deg', duration: 9, peach: true },
    { code: 'OD', name: 'Observation / data', left: '85%', top: '55%', tilt: '-4deg', duration: 7.5 },
    { code: 'Fq', name: 'Feedback with question', left: '12%', top: '82%', tilt: '-3deg', duration: 8.5 },
    { code: 'SK', name: 'Scientific knowledge', left: '78%', top: '84%', tilt: '6deg', duration: 9.5, peach: true },
];

const FloatingCodes = () => (
    <Box aria-hidden="true" sx={{ display: { xs: 'none', lg: 'block' }, position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {FLOATING_CODES.map((chip, i) => {
            const popAt = DRAW_AT + 0.2 + i * 0.12;
            return (
                <Box
                    key={chip.code}
                    sx={{
                        '--tilt': chip.tilt,
                        position: 'absolute',
                        left: chip.left,
                        top: chip.top,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 999,
                        bgcolor: chip.peach ? tokens.highlightTint : tokens.surface,
                        boxShadow: `inset 0 0 0 1px ${chip.peach ? 'rgba(245, 158, 108, 0.45)' : tokens.line}, ${tokens.shadow}`,
                        transform: `rotate(${chip.tilt})`,
                        animation: `${popIn} 500ms ${tokens.ease} ${popAt}s both, ${bob} ${chip.duration}s ease-in-out ${popAt + 0.5}s infinite`,
                        [REDUCED_MOTION]: { animation: 'none' },
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            px: 0.75,
                            borderRadius: 1.5,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            bgcolor: chip.peach ? tokens.highlight : tokens.accent,
                            color: chip.peach ? tokens.ink : '#FFFFFF',
                        }}
                    >
                        {chip.code}
                    </Box>
                    <Box component="span" sx={{ fontSize: '0.8125rem', fontWeight: 500, color: tokens.ink }}>
                        {chip.name}
                    </Box>
                </Box>
            );
        })}
    </Box>
);

// One headline word that rises in after `index` steps
const Word = ({ children, index }) => (
    <Box
        component="span"
        sx={{
            display: 'inline-block',
            animation: `${wordIn} 700ms ${tokens.ease} ${0.15 + index * WORD_STEP}s both`,
            [REDUCED_MOTION]: { animation: 'none' },
        }}
    >
        {children}
    </Box>
);

// Focal point 1 of 2: the headline rises in word by word, a highlighter draws under the last two
// words, and the floating codes pop in around it, over one soft glow
const Hero = () => (
    <Box
        component="section"
        aria-labelledby="hero-title"
        sx={{ position: 'relative', overflow: 'hidden', mt: '-72px', pt: { xs: 18, md: 26 }, pb: { xs: 8, md: 10 } }}
    >
        <Glow fade />
        <FloatingCodes />

        <Container sx={{ position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Reveal>
                <Chip
                    sx={{
                        mb: 3,
                        gap: 1,
                        bgcolor: tokens.surface,
                        boxShadow: `inset 0 0 0 1px ${tokens.line}, 0 4px 12px -4px rgba(30, 58, 95, 0.12)`,
                    }}
                >
                    <Box component="span" aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: 999, bgcolor: tokens.highlight }} />
                    Classroom discourse analysis
                </Chip>
            </Reveal>
            <Typography id="hero-title" variant="h1" component="h1" sx={{ maxWidth: '15ch' }}>
                {LEAD_WORDS.map((word, i) => (
                    <React.Fragment key={word}>
                        <Word index={i}>{word}</Word>{' '}
                    </React.Fragment>
                ))}
                {/* Highlighter stroke, like a marked-up transcript */}
                <Box
                    component="span"
                    sx={{
                        color: tokens.accent,
                        backgroundImage: 'linear-gradient(rgba(245, 158, 108, 0.45), rgba(245, 158, 108, 0.45))',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: '0 88%',
                        backgroundSize: '100% 34%',
                        boxDecorationBreak: 'clone',
                        WebkitBoxDecorationBreak: 'clone',
                        px: '0.06em',
                        animation: `${draw} 700ms ${tokens.ease} ${DRAW_AT}s both`,
                        [REDUCED_MOTION]: { animation: 'none' },
                    }}
                >
                    {MARKED_WORDS.map((word, i) => (
                        <React.Fragment key={word}>
                            {i > 0 && ' '}
                            <Word index={LEAD_WORDS.length + i}>{word}</Word>
                        </React.Fragment>
                    ))}
                </Box>
            </Typography>
            <Reveal delay={500}>
                <Typography variant="subtitle1" component="p" sx={{ mt: { xs: 3, md: 4 }, maxWidth: 600, mx: 'auto' }}>
                    Upload a lesson transcript. ADAS labels every utterance with what it does and the reasoning behind it,
                    then turns the lesson into a dashboard you can explore.
                </Typography>
            </Reveal>
            <Reveal delay={650} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mt: { xs: 4, md: 5 } }}>
                <LandingButton to="/register" arrow>
                    Analyze a lesson
                </LandingButton>
                <LandingButton variant="secondary" href="#how-it-works">
                    See how it works
                </LandingButton>
            </Reveal>
        </Container>
    </Box>
);

export default Hero;
